import { eachDayOfInterval, endOfDay, format, isSameDay, parseISO, startOfDay } from 'date-fns';
import { AdministrationSlot, OrderWithSlots } from '../../types/treatmentSheet';

/**
 * Pure helpers for the treatment sheet's day ladder. Extracted so the tab
 * range, the union column derivation, and the per-row slot matching are
 * unit-testable without the 900-line grid component.
 */

export interface DayTab {
  /** Stable identity for the query key: 'yyyy-MM-dd' (local). */
  dayKey: string;
  /** "Today" for today, otherwise e.g. "Mon 8/11". */
  label: string;
  windowStart: Date;
  windowEnd: Date;
  /**
   * TRAP: tabs can be FUTURE days (duplicate-to-date pre-fills them), so
   * `!isToday` does NOT mean "past" — prior-day logic must compare
   * `dayKey < todayKey`.
   */
  isToday: boolean;
}

export const dayKeyOf = (d: Date): string => format(d, 'yyyy-MM-dd');

/**
 * One tab per calendar day from the admit date through today (inclusive,
 * local clock — the hospital module is online-only and staff are on-site).
 * A discharged sheet's ladder ends at its discharge day instead. When the
 * latest order end lies BEYOND the anchor (duplicate-to-date can pre-fill a
 * future day), the ladder extends to it so the pre-filled day stays reachable.
 */
export function buildDayTabs(
  admitDateISO: string,
  now: Date,
  dischargeDateISO?: string | null,
  lastOrderEndISO?: string | null,
): DayTab[] {
  const first = startOfDay(parseISO(admitDateISO));
  let lastAnchor = dischargeDateISO ? parseISO(dischargeDateISO) : now;
  if (lastOrderEndISO) {
    const lastOrderEnd = parseISO(lastOrderEndISO);
    if (lastOrderEnd > lastAnchor) lastAnchor = lastOrderEnd;
  }
  // Guard degenerate data (admit in the future): show at least the anchor day.
  const last = startOfDay(lastAnchor < first ? first : lastAnchor);
  return eachDayOfInterval({ start: first, end: last }).map((day) => ({
    dayKey: dayKeyOf(day),
    label: isSameDay(day, now) ? 'Today' : format(day, 'EEE M/d'),
    windowStart: startOfDay(day),
    windowEnd: endOfDay(day),
    isToday: isSameDay(day, now),
  }));
}

/**
 * Column timestamps = sorted unique union of EVERY order's slots. The grid
 * used to take the FIRST order's slots as the headers, which misaligns the
 * moment orders have different schedules (a Q12H first order gave 2 columns
 * even when a Q1H order below had 24).
 */
export function buildUnionColumns(orders: OrderWithSlots[]): string[] {
  const set = new Set<string>();
  for (const order of orders) {
    for (const slot of order.slots) set.add(slot.scheduledAt);
  }
  return Array.from(set).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
}

/**
 * The order's slot for a given union column, or null (spacer cell). Exact
 * string match is safe — columns and slots come from the same computeSlots
 * response.
 */
export function slotForColumn(order: OrderWithSlots, columnTs: string): AdministrationSlot | null {
  return order.slots.find((s) => s.scheduledAt === columnTs) ?? null;
}

/**
 * Legacy continuous orders (created before day-scoping) have no endAt and
 * appear on every day tab until discontinued — badge them so staff can tell
 * why they never expire.
 */
export const isLegacyContinuous = (order: { endAt?: string | null }): boolean => !order.endAt;
